import { api } from '@/api';
import { useDateTimeStore } from '@/stores/dateTimeStore.ts';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { QaEquipmentRtSystemDto, TenantLicenseDataDto, UnicheckDeviceDto } from '@/api/generator';
import { toUserTime } from '@/components/formatters/utils';
import { isIotDeviceWithoutLicense } from '@/utils/rtSystem';
import { I18nKey } from '@/utils/typings';

export const useLicenseStore = defineStore('license', () => {
  const license = ref<TenantLicenseDataDto>();

  const dateTimeStore = useDateTimeStore();
  const { currentDateTime } = storeToRefs(dateTimeStore);

  const LICENSE_EXPIRY_WARNING_WEEKS = 12;

  const daysToLicenseExpiration = computed(() => license.value?.licensePlan?.daysLeft ?? 0);
  const licenseIsAboutToExpire = computed(
    () => daysToLicenseExpiration.value <= LICENSE_EXPIRY_WARNING_WEEKS * 7,
  );
  const licenseHasExpired = computed(() => daysToLicenseExpiration.value <= 0);

  type UnicheckDeviceExpirationInfo = Pick<
    UnicheckDeviceDto,
    'masterDeviceId' | 'id' | 'iuid' | 'name' | 'model' | 'subModel' | 'serialNumber'
  > & { daysToExpiration: number };

  const deviceLicensesAboutToExpire = computed(() => {
    const deviceMap = new Map<string, UnicheckDeviceExpirationInfo>();

    if (!license.value?.devices || !currentDateTime.value) {
      return deviceMap;
    }

    for (const device of license.value.devices) {
      const deviceExpirationDateTime = toUserTime(device.expirationDate);

      if (!device.masterDeviceId || !deviceExpirationDateTime) {
        continue;
      }

      const warningDateTime = deviceExpirationDateTime.minus({
        weeks: LICENSE_EXPIRY_WARNING_WEEKS,
      });

      // Only include devices that are about to expire
      if (warningDateTime.toMillis() < currentDateTime.value.toMillis()) {
        const daysToExpiration = Math.ceil(deviceExpirationDateTime.diffNow('days').days);
        if (daysToExpiration > 0) {
          deviceMap.set(device.masterDeviceId, {
            masterDeviceId: device.masterDeviceId,
            id: device.id!,
            iuid: device.iuid,
            name: device.name!,
            model: device.model,
            subModel: device.subModel,
            serialNumber: device.serialNumber,
            daysToExpiration,
          });
        }
      }
    }

    return deviceMap;
  });

  const allDeviceLicensesMasterDeviceIds = computed(() => {
    if (!license.value?.devices) {
      return [];
    }
    return license.value.devices
      .filter((device) => device.masterDeviceId)
      .map((device) => device.masterDeviceId);
  });
  const expiredDeviceLicenses = computed(() => {
    const deviceMap = new Map<string, UnicheckDeviceExpirationInfo>();

    if (!license.value?.devices || !currentDateTime.value) {
      return deviceMap;
    }

    for (const device of license.value.devices) {
      if (!device.masterDeviceId || !device.expirationDate) {
        continue;
      }

      const deviceExpirationDateTime = toUserTime(device.expirationDate);

      // Only include devices that have expired
      if (
        deviceExpirationDateTime &&
        deviceExpirationDateTime.diffNow('milliseconds').milliseconds <= 0
      ) {
        const daysToExpiration = Math.round(deviceExpirationDateTime.diffNow('days').days);

        deviceMap.set(device.masterDeviceId, {
          masterDeviceId: device.masterDeviceId,
          id: device.id!,
          iuid: device.iuid,
          name: device.name!,
          model: device.model,
          subModel: device.subModel,
          serialNumber: device.serialNumber,
          daysToExpiration,
        });
      }
    }

    return deviceMap;
  });
  /** Translation key describing the device's license problem, or undefined when it has none. */
  function deviceLicenseIssue(device: QaEquipmentRtSystemDto): I18nKey | undefined {
    if (device.masterDeviceId && expiredDeviceLicenses.value.has(device.masterDeviceId)) {
      return 'TheDevicesLicenseHasExpired';
    }
    if (isIotDeviceWithoutLicense(device, allDeviceLicensesMasterDeviceIds.value)) {
      return 'TheDevicesLicenseIsMissing';
    }
    return undefined;
  }

  const earliestExpirationDays = computed(() => {
    const deviceExpirationDays = Array.from(deviceLicensesAboutToExpire.value.values()).map(
      (dl) => dl.daysToExpiration,
    );

    return Math.min(daysToLicenseExpiration.value, ...deviceExpirationDays) || 0;
  });

  function hasLicenseOption(option: string) {
    return license.value?.options?.some((o) => o.option == option);
  }

  async function fetchLicenseData() {
    const { result } = await api.licenseApi.getLicenseDataInfo();
    if (result) {
      license.value = result;
    }
  }

  const licenseHasIssues = computed(
    () =>
      licenseIsAboutToExpire.value ||
      licenseHasExpired.value ||
      deviceLicensesAboutToExpire.value.size ||
      expiredDeviceLicenses.value.size,
  );

  return {
    hasLicenseOption,
    deviceLicenseIssue,
    fetchLicenseData,
    licenseHasIssues,
    licenseIsAboutToExpire,
    daysToLicenseExpiration,
    deviceLicensesAboutToExpire,
    expiredDeviceLicenses,
    earliestExpirationDays,
    licenseHasExpired,
    allDeviceLicensesMasterDeviceIds,
  };
});
